import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EditArticleDialog } from "@/components/EditArticleDialog";
import { AddArticleDialog } from "@/components/AddArticleDialog";
import { useArticles } from "@/hooks/useArticles";
import { FAQManager } from "@/components/FAQManager";
import { useAdminAuth } from "@/components/AdminAuth";
import { useChateaux } from "@/hooks/useChateaux";
import { ChateauFormDialog } from "@/components/ChateauFormDialog";
import { useSumos, Sumo } from "@/hooks/useSumos";
import { SumoEditor } from "@/components/SumoEditor";
import { SumoFormDialog } from "@/components/SumoFormDialog";
import { ReservationsManager } from "@/components/admin/ReservationsManager";

import { MultiImageUpload } from "@/components/MultiImageUpload";
import { ImageGallery } from "@/components/ImageGallery";
import { 
  Settings, 
  FileText, 
  Castle, 
  Image, 
  Video, 
  Plus, 
  Edit, 
  Trash2,
  Eye,
  Lock,
  X,
  BarChart3,
  Zap,
  CalendarCheck
} from "lucide-react";
import { AnalyticsDashboard } from "./AnalyticsDashboard";

interface AdminPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AdminPanel = ({ isOpen, onClose }: AdminPanelProps) => {
  const [editArticleOpen, setEditArticleOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<any>(null);
  const [addArticleOpen, setAddArticleOpen] = useState(false);
  const [selectedChateau, setSelectedChateau] = useState<string>("");
  const { isAdminLoggedIn, loginAdmin, isLoading } = useAdminAuth();
  const { chateaux, fetchChateaux, createChateau, updateChateau, deleteChateau, toggleVisibility } = useChateaux();
  const { articles, fetchArticles, deleteArticle, togglePublished } = useArticles();
  const { sumos, fetchSumos, createSumo, updateSumo, deleteSumo } = useSumos();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");

  // Wrapper functions pour adapter les signatures
  const handleCreateChateau = async (chateauData: any) => {
    await createChateau(chateauData);
  };

  const handleUpdateChateau = async (chateauData: any) => {
    const { id, ...updates } = chateauData;
    await updateChateau(id, updates);
  };

  // Charger tous les châteaux, articles et sumos (visibles et cachés) pour l'admin
  useEffect(() => {
    if (isAdminLoggedIn && isOpen) {
      console.log('📋 Admin Panel: Chargement des données...');
      fetchChateaux(true); // includeHidden = true pour l'admin
      fetchArticles(true); // includeUnpublished = true pour l'admin
      fetchSumos(true); // includeHidden = true pour l'admin
    }
  }, [isAdminLoggedIn, isOpen]);

  // Gestion de la touche Escape pour fermer la popup
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleLogin = async () => {
    const success = await loginAdmin(email, password);
    if (success) {
      setAuthError("");
    } else {
      setAuthError("Email ou mot de passe incorrect");
    }
  };


  if (!isOpen) return null;

  // Login Form - Centré au milieu de la page
  if (!isAdminLoggedIn) {
    return (
      <>
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 cursor-pointer" 
          onClick={onClose}
          aria-label="Fermer la popup en cliquant à l'extérieur"
        />
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4 min-h-screen pointer-events-none">
          <div className="bg-background rounded-lg shadow-2xl w-full max-w-md relative mx-auto border pointer-events-auto">
            {/* Header avec X */}
            <div className="flex items-center justify-between p-6 border-b bg-gradient-primary text-white rounded-t-lg">
              <div className="flex items-center">
                <Lock className="w-6 h-6 mr-3" />
                <h2 className="text-xl font-display font-bold">
                  Connexion Admin
                </h2>
              </div>
              <button 
                onClick={onClose}
                className="text-white hover:text-gray-300 transition-colors w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20"
                title="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Login Form */}
            <div className="p-6 space-y-4 text-foreground">
              <div>
                <Label htmlFor="email" className="text-foreground">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="lechateaudeclementine@gmail.com"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-foreground">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Votre mot de passe"
                />
              </div>
              {authError && (
                <p className="text-red-500 text-sm">{authError}</p>
              )}
              <Button onClick={handleLogin} className="w-full">
                Se connecter
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Main Admin Panel (authenticated)

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 cursor-pointer" 
        onClick={onClose}
        aria-label="Fermer la popup en cliquant à l'extérieur"
      />
      <div className="fixed inset-4 bg-background rounded-lg shadow-2xl z-50 overflow-hidden pointer-events-auto">
        <div className="flex flex-col h-full">
          
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b bg-gradient-primary text-white">
            <div className="flex items-center">
              <Settings className="w-6 h-6 mr-3" />
              <h2 className="text-2xl font-display font-bold">Panneau Administrateur</h2>
            </div>
            <Button variant="outline" onClick={onClose} className="text-white border-white hover:bg-white/20">
              Fermer
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <Tabs defaultValue="reservations" className="w-full">
              <TabsList className="grid w-full grid-cols-8">
                <TabsTrigger value="reservations">
                  <CalendarCheck className="w-4 h-4 mr-2" />
                  Réservations
                </TabsTrigger>
                <TabsTrigger value="analytics">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Analytics
                </TabsTrigger>
                <TabsTrigger value="articles">
                  <FileText className="w-4 h-4 mr-2" />
                  Articles
                </TabsTrigger>
                <TabsTrigger value="chateaux">
                  <Castle className="w-4 h-4 mr-2" />
                  Châteaux
                </TabsTrigger>
                <TabsTrigger value="sumos">
                  <Zap className="w-4 h-4 mr-2" />
                  Sumos
                </TabsTrigger>
                <TabsTrigger value="faq">
                  <FileText className="w-4 h-4 mr-2" />
                  FAQ
                </TabsTrigger>
                <TabsTrigger value="media">
                  <Image className="w-4 h-4 mr-2" />
                  Médias
                </TabsTrigger>
                <TabsTrigger value="nouveau-chateau">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau Château
                </TabsTrigger>
              </TabsList>

              {/* Reservations Tab */}
              <TabsContent value="reservations" className="space-y-6">
                <ReservationsManager />
              </TabsContent>

              {/* Analytics Tab */}
              <TabsContent value="analytics" className="space-y-6">
                <AnalyticsDashboard />
              </TabsContent>

              {/* Articles Tab */}
              <TabsContent value="articles" className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-display font-semibold">Gestion des Articles</h3>
                  <Button onClick={() => setAddArticleOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un Article
                  </Button>
                </div>
                
                <Card>
                  <CardContent className="p-6">
                    <p className="text-muted-foreground mb-4">
                      Gérez vos articles de blog, créez du contenu optimisé SEO, et suivez les performances.
                    </p>
                    <div className="grid gap-4">
                      {articles.map((article) => (
                        <div key={article.id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold">{article.title}</h4>
                              <Badge variant={article.is_published ? "default" : "secondary"}>
                                {article.is_published ? "Publié" : "Brouillon"}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {new Date(article.created_at).toLocaleDateString('fr-FR')} • {article.read_time} min de lecture
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setSelectedArticle(article);
                                setEditArticleOpen(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => togglePublished(article.id, !article.is_published)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => {
                                if (confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
                                  deleteArticle(article.id);
                                }
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      
                      {articles.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          Aucun article pour le moment. Commencez par en créer un !
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

          {/* Châteaux Tab avec Upload dans les détails */}
          <TabsContent value="chateaux" className="space-y-6">
            <h3 className="text-xl font-display font-semibold">Modifier les Détails des Châteaux</h3>
            
            <div className="grid grid-cols-1 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Sélectionner un Château</CardTitle>
                </CardHeader>
                <CardContent>
                  <Label className="text-sm font-medium mb-2">Château à modifier</Label>
                  <select 
                    className="w-full px-3 py-2 border border-border rounded-md"
                    value={selectedChateau}
                    onChange={(e) => setSelectedChateau(e.target.value)}
                  >
                    <option value="">Choisir un château...</option>
                    {chateaux.map((chateau) => (
                      <option key={chateau.id} value={chateau.id}>
                        {chateau.nom} - {chateau.prix}
                      </option>
                    ))}
                  </select>
                </CardContent>
              </Card>

              {selectedChateau && (
                <Card>
                  <CardHeader>
                    <CardTitle>Modifier les Détails et Images</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="details">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="details">Détails</TabsTrigger>
                        <TabsTrigger value="images">Images</TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="details" className="space-y-4 mt-4">
                        {(() => {
                          const chateau = chateaux.find(c => c.id === selectedChateau);
                          if (!chateau) return null;
                          
                          return (
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label>Nom</Label>
                                  <p className="text-sm font-medium p-2 bg-muted rounded">{chateau.nom}</p>
                                </div>
                                <div>
                                  <Label>Prix</Label>
                                  <p className="text-sm font-medium p-2 bg-muted rounded">{chateau.prix}</p>
                                </div>
                                <div>
                                  <Label>Taille</Label>
                                  <p className="text-sm font-medium p-2 bg-muted rounded">{chateau.taille}</p>
                                </div>
                                <div>
                                  <Label>Capacité</Label>
                                  <p className="text-sm font-medium p-2 bg-muted rounded">{chateau.capacite}</p>
                                </div>
                                <div>
                                  <Label>Âge recommandé</Label>
                                  <p className="text-sm font-medium p-2 bg-muted rounded">{chateau.age_recommande}</p>
                                </div>
                                <div>
                                  <Label>Statut</Label>
                                  <Badge variant={chateau.is_visible ? "default" : "secondary"}>
                                    {chateau.is_visible ? "Visible" : "Masqué"}
                                  </Badge>
                                </div>
                              </div>
                              
                              <div>
                                <Label>Description</Label>
                                <p className="text-sm p-3 bg-muted rounded leading-relaxed">{chateau.description}</p>
                              </div>
                              
                              <div>
                                <Label>Caractéristiques</Label>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {Array.isArray(chateau.caracteristiques) ? 
                                    chateau.caracteristiques.map((carac, index) => (
                                      <Badge key={index} variant="outline">{carac}</Badge>
                                    )) : 
                                    <Badge variant="outline">Aucune caractéristique</Badge>
                                  }
                                </div>
                              </div>
                              
                              <div className="flex gap-2 pt-4 border-t">
                                <ChateauFormDialog
                                  chateau={chateau}
                                  onSave={handleUpdateChateau}
                                  trigger={
                                    <Button variant="outline" className="flex-1">
                                      <Edit className="w-4 h-4 mr-2" />
                                      Modifier
                                    </Button>
                                  }
                                  title="Modifier le Château"
                                />
                                <Button 
                                  variant="outline" 
                                  onClick={() => {
                                    toggleVisibility(chateau.id, !chateau.is_visible);
                                  }}
                                  className="flex-1"
                                >
                                  <Eye className="w-4 h-4 mr-2" />
                                  {chateau.is_visible ? 'Masquer' : 'Afficher'}
                                </Button>
                                <Button 
                                  variant="destructive" 
                                  onClick={() => {
                                    if (confirm('Êtes-vous sûr de vouloir supprimer ce château ?')) {
                                      deleteChateau(selectedChateau);
                                      setSelectedChateau("");
                                    }
                                  }}
                                  className="flex-1"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Supprimer
                                </Button>
                              </div>
                            </div>
                          );
                        })()}
                      </TabsContent>
                      
                      <TabsContent value="images" className="space-y-4 mt-4">
                        <MultiImageUpload 
                          chateauId={selectedChateau}
                          onImagesUploaded={(imageUrls) => {
                            console.log('Images uploadées:', imageUrls);
                          }}
                          maxFiles={10}
                        />
                        
                        <div className="mt-6">
                          <ImageGallery 
                            chateauId={selectedChateau}
                            onImageDeleted={() => {
                              console.log('Image supprimée');
                            }}
                          />
                        </div>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

              {/* Sumos Tab */}
              <TabsContent value="sumos" className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-display font-semibold">Gestion des Sumos</h3>
                  <SumoFormDialog
                    onSave={async (sumoData) => {
                      await createSumo(sumoData as Omit<Sumo, 'id' | 'created_at' | 'updated_at'>);
                    }}
                    trigger={
                      <Button>
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter un Sumo
                      </Button>
                    }
                    title="Ajouter un Nouveau Sumo"
                  />
                </div>
                
                <div className="grid gap-6">
                  {sumos.length > 0 ? (
                    sumos.map((sumo) => (
                      <SumoEditor
                        key={sumo.id}
                        sumo={sumo}
                        sumoId={sumo.id}
                        onSave={async (sumoData) => {
                          await updateSumo(sumo.id, sumoData);
                        }}
                        onDelete={async (sumoId) => {
                          await deleteSumo(sumoId);
                        }}
                        onToggleVisibility={async (sumoId, isVisible) => {
                          await updateSumo(sumoId, { is_visible: isVisible });
                        }}
                        isHidden={!sumo.is_visible}
                      />
                    ))
                  ) : (
                    <Card>
                      <CardContent className="p-6 text-center">
                        <p className="text-muted-foreground">Aucun sumo disponible pour le moment.</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* FAQ Tab */}
              <TabsContent value="faq" className="space-y-6">
                <FAQManager />
              </TabsContent>

          {/* Médias Tab - Remplacer l'ancien ImageUpload par MultiImageUpload */}
          <TabsContent value="media" className="space-y-6">
            <h3 className="text-xl font-display font-semibold">Gestion des Médias</h3>
            
            <div className="grid grid-cols-1 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Image className="w-5 h-5 mr-2" />
                    Upload Multiple d'Images
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Château concerné</Label>
                    <select 
                      className="w-full px-3 py-2 border border-border rounded-md"
                      value={selectedChateau}
                      onChange={(e) => setSelectedChateau(e.target.value)}
                    >
                      <option value="">Sélectionner un château...</option>
                      {chateaux.map((chateau) => (
                        <option key={chateau.id} value={chateau.id}>
                          {chateau.nom}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {selectedChateau && (
                    <>
                      <MultiImageUpload 
                        chateauId={selectedChateau}
                        onImagesUploaded={(imageUrls) => {
                          console.log('Images uploadées:', imageUrls);
                          // Rafraîchir la galerie d'images
                        }}
                        maxFiles={10}
                      />
                      
                      <div className="mt-6">
                        <ImageGallery 
                          chateauId={selectedChateau}
                          onImageDeleted={() => {
                            console.log('Image supprimée');
                          }}
                        />
                      </div>
                    </>
                  )}
                  
                  {!selectedChateau && (
                    <p className="text-sm text-muted-foreground">
                      Sélectionnez d'abord un château pour gérer ses images.
                    </p>
                  )}
                </CardContent>
              </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Video className="w-5 h-5 mr-2" />
                        Ajouter des Vidéos
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>Château concerné</Label>
                        <select className="w-full px-3 py-2 border border-border rounded-md">
                          <option>Sélectionner un château...</option>
                          {chateaux.map((chateau) => (
                            <option key={chateau.id} value={chateau.id}>
                              {chateau.nom}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>URL de la vidéo</Label>
                        <Input placeholder="https://youtube.com/watch?v=..." />
                      </div>
                      <div>
                        <Label>Titre de la vidéo</Label>
                        <Input placeholder="Titre descriptif" />
                      </div>
                      <Button className="w-full">
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter la Vidéo
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Nouveau Château Tab */}
              <TabsContent value="nouveau-chateau" className="space-y-6">
                <h3 className="text-xl font-display font-semibold">Ajouter un Nouveau Château</h3>
                
                <Card>
                  <CardContent className="p-6">
                    <ChateauFormDialog
                      onSave={handleCreateChateau}
                      trigger={
                        <Button className="w-full">
                          <Plus className="w-4 h-4 mr-2" />
                          Créer un Nouveau Château
                        </Button>
                      }
                      title="Ajouter un Nouveau Château"
                    />
                  </CardContent>
                </Card>
                
                {/* Liste des châteaux existants */}
                <Card>
                  <CardHeader>
                    <CardTitle>Châteaux Existants</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4">
                      {chateaux.map((chateau) => (
                        <div key={chateau.id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div className="flex-1">
                            <h4 className="font-semibold">{chateau.nom}</h4>
                            <p className="text-sm text-muted-foreground">
                              {chateau.prix} - {chateau.taille} - {chateau.capacite}
                            </p>
                            {!chateau.is_visible && (
                              <Badge variant="secondary" className="mt-1">Masqué</Badge>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <ChateauFormDialog
                              chateau={chateau}
                              onSave={handleUpdateChateau}
                              trigger={
                                <Button variant="outline" size="sm">
                                  <Edit className="w-4 h-4" />
                                </Button>
                              }
                              title={`Modifier ${chateau.nom}`}
                            />
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => toggleVisibility(chateau.id, !chateau.is_visible)}
                            >
                              {chateau.is_visible ? <Eye className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => {
                                if (confirm('Êtes-vous sûr de vouloir supprimer ce château ?')) {
                                  deleteChateau(chateau.id);
                                }
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <AddArticleDialog 
        open={addArticleOpen} 
        onOpenChange={setAddArticleOpen}
      />
      
      <EditArticleDialog
        open={editArticleOpen}
        onOpenChange={setEditArticleOpen}
        article={selectedArticle}
      />
    </>
  );
};
